import { useCallback } from 'react';
import type { MutateOptions, UseMutationResult } from '../react-query';
import type { ErrorType } from '../utils/fetch';
import { isVersionError } from '../utils/isVersionError';

const MAX_RETRY_COUNT = 3;

type OptionsWithRetry<Data, Error, Variables, Context> = MutateOptions<
  Data,
  Error,
  Variables,
  Context
> & {
  getRetryVariables: (
    error: ErrorType<{ currentVersion?: number }>,
    variables: Variables,
  ) => Variables;
};

function useMutationWithVersionError<Data, Error, Variables, Context>(
  mutationResult: UseMutationResult<Data, Error, Variables, Context>,
) {
  const { mutateAsync, mutate, ...rest } = mutationResult;

  const mutateWithRetry = useCallback(
    (
      variables: Variables,
      options: OptionsWithRetry<Data, Error, Variables, Context>,
      retryCount = 0,
    ) => {
      const defaultOnError = options.onError;

      mutate(variables, {
        ...options,
        onError: (error, _variables, mutationResult, context) => {
          const versionError = error as ErrorType<{ currentVersion?: number }>;

          if (isVersionError(versionError) && retryCount < MAX_RETRY_COUNT) {
            mutateWithRetry(
              options.getRetryVariables(versionError, variables),
              options,
              retryCount + 1,
            );
          } else if (defaultOnError) {
            defaultOnError(error, _variables, mutationResult, context);
          }
        },
      });
    },
    [mutate],
  );

  const mutateAsyncWithRetry = useCallback(
    async (
      variables: Variables,
      options: OptionsWithRetry<Data, Error, Variables, Context>,
      retryCount = 0,
    ): Promise<Data> => {
      try {
        return await mutateAsync(variables, options);
      } catch (error) {
        const versionError = error as ErrorType<{ currentVersion?: number }>;

        if (isVersionError(versionError) && retryCount < MAX_RETRY_COUNT) {
          return mutateAsyncWithRetry(
            options.getRetryVariables(versionError, variables),
            options,
            retryCount + 1,
          );
        }

        throw error;
      }
    },
    [mutateAsync],
  );

  return { ...rest, mutate: mutateWithRetry, mutateAsync: mutateAsyncWithRetry };
}

export default useMutationWithVersionError;
